import React, { useEffect, useState } from 'react';
import Avatar from 'react-avatar';
import { Link } from 'react-router-dom';
import { Grid, Header, Icon, Popup, Portal, Ref } from 'semantic-ui-react';
import { PhoneUtil, StringUtils } from 'utils';
import "./index.scss"
import ButtonLink from 'dyl-components/atoms/ButtonLink';
import { DragOverlay, useDraggable } from '@dnd-kit/core';
import DragHandle from 'dyl-components/atoms/DragHandle';
import DraggableCall from '../DraggableCall';
import RestrictedView from '../Restricted';
import { USER_PERMISSIONS, PRODUCT_PERMISSIONS } from '../Restricted/PERMISSIONS';

const STAGE_ICONS = {
    opportunity: "fa-solid fa-funnel-dollar",
    lead: "fa-solid fa-id-card",
}

const OfficeViewMiddleHeader = ({
    person,
    onRemovePerson,
    onEmailClick,
    onTaskClick,
    onEventClick,
    onHotlistClick,
    isInHotlist,
    hasIntegration = false,
    logActivityForm,
    isLogActivityModalOpen,
    setIsLogActivityModalOpen,
    isCallActivityFormOpen,
    setIsCallActivityFormOpen,
    callState,
    unknown,
    onNewContactClick,
    onClickToDial,
    isPhoneSystemSupported,
    isInternalCall,
    currentCall,
    currentTime,
    isCallHeld,
    userPhoneCreds,
    currentCallUUID
}) => {
    const [isEmailDisabled, setIsEmailDisabled] = useState(false);

    useEffect(() => {
        const isEmailDisabledAux = person?.contact_id ? (
            !person?.contact_details?.email
        ) : (
            !person?.account_details?.email
        )
        setIsEmailDisabled(isEmailDisabledAux);
    }, [hasIntegration, person])
    
    const isConferenceRoom = currentCall?.company_destination?.substr(0,1) !== '7' || currentCall?.call_type !== "dialphone";

    const isCallActive = callState === "active";
    const isCallRinging = callState === "ringing";
    const isCallNotIdle = isCallActive || isCallRinging;
    const isUnknown = unknown.unknown;
    const phone = PhoneUtil.contactLeg(currentCall, userPhoneCreds)?.phone 
    const hasPhone = person?.contact_details?.phone.length > 0 || person?.account_details?.phone.length > 0 ;
    const { isDragging, setNodeRef, attributes, listeners, setActivatorNodeRef } = useDraggable({
        id: currentCallUUID,
        disabled: isInternalCall,
        data: {
            phone,
            uuid: currentCallUUID,
            caller_id_name: !isUnknown ? person.name : unknown.name || "Unknown",
            source: "office-view-call-box"
        }
    });
    const dragHandle = <DragHandle ref={setActivatorNodeRef} isDragging={isDragging} {...listeners} {...attributes} disabled={isInternalCall} />;
    return (
        <div>
            <div ref={setNodeRef} data-uuid={currentCallUUID}>
                <Grid columns='equal' verticalAlign='middle'>
                    {isCallNotIdle && (
                        <Grid.Column width={1} textAlign='left'>
                            {dragHandle}
                        </Grid.Column>
                    )}
                    <Grid.Column>
                        <div style={{ display: 'flex' }} data-uuid={currentCallUUID}>
                            {!isUnknown ?
                                <Avatar
                                    size='5em'
                                    round
                                    maxInitials={2}
                                    name={person.name}
                                    style={{ marginRight: 15 }} />
                                : isConferenceRoom ?
                                    <div className='OfficeView__roundAvatar'>
                                        <Icon name="user" size='big' style={{ marginLeft: "3px" }} className='onHoverOnClick__white' />
                                    </div> : ""}
                            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                                <div style={{ display: 'flex' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ width: 'fit-content' }}>
                                            {!isUnknown ? (
                                                <Header as={"h1"} className='OfficeView__headerName'>
                                                    <Link to={person.contact_id ? `/contact/${person.contact_id}` : person.account_id ? `/account/${person.account_id}` : `/settings/users/${person.user_id}/general`} target='_blank' rel='noopener noreferrer'>
                                                        {person.name}
                                                    </Link>
                                                </Header>
                                            ) : (
                                                <Header as={"h1"} className={`OfficeView__${isConferenceRoom ? "unknownName" : "conferenceRoom"}`}>
                                                    {!unknown.name ? 'Unknown' : unknown.name}
                                                </Header>
                                            )}
                                        </div>
                                    </div>
                                    <Icon name='x' size='big' className='OfficeView__closeHeaderIcon' disabled={isCallNotIdle} onClick={onRemovePerson} style={{ cursor: "pointer" }} />
                                </div>
                                {(!isInternalCall && !isUnknown) &&
                                    <div style={{ display: 'flex', gap: 15 }}>
                                        <div>
                                            <Icon
                                                className={`fa-solid ${person?.contact_id ?
                                                    ("fa-user") :
                                                    (person?.account_details?.account_type === "business" ? "fa-buildings" : "fa-house-chimney")
                                                }`}
                                                style={{ marginRight: 6 }} 
                                            />
                                            <strong>{person.contact_id ? "Contact" : "Account"}</strong>
                                        </div>
                                        {!isInternalCall && person?.contact_id ? (
                                            <>
                                                {person.contact_details?.job_title && <span>{person.contact_details?.job_title}</span>}
                                                <div>
                                                    <Icon className={STAGE_ICONS[person.contact_details?.pipeline_stage]} style={{ marginRight: 6 }} />
                                                    <strong>{StringUtils.capitalize(person.contact_details?.pipeline_stage || "") || ""}</strong>
                                                </div>
                                            </>
                                        ) : (person?.account_details?.customer && (
                                            <div>
                                                <Icon className='fa-solid fa-user-crown' style={{ marginRight: 6 }} />
                                                <strong>Customer</strong>
                                            </div>
                                        ))}
                                    </div>
                                }
                                {!isConferenceRoom &&
                                    <div style={{ display: 'flex', gap: 15, marginTop: 10 }}>
                                        <div>
                                            <Header as={"h3"}> Ext. {currentCall?.company_destination || ""}</Header>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </Grid.Column>
                </Grid>
            </div>
            <Portal open={isDragging}>
                <DragOverlay>
                    <DraggableCall 
                        dragHandle={dragHandle}
                        personDisplay={!isUnknown ? person.name : unknown.name || "Unknown"}
                        isRinging={isCallRinging}
                        isHeld={isCallHeld}
                        duration={isCallActive && PhoneUtil.timeTicker(currentCall?.connected && currentTime > 0 ? currentTime - currentCall?.connected + (currentCall?.drift || 0) : 0 )}
                    />
                </DragOverlay>
            </Portal>
            {!isUnknown &&
                <div style={{ display: 'flex', gap: 15, marginTop: 15, marginLeft: 10 }}>
                    {isPhoneSystemSupported ? (
                        <ButtonLink
                            onClick={onClickToDial}
                            disabled={isCallNotIdle || !hasPhone}
                        >
                            <Icon
                                className="fa-solid fa-phone OfficeView__icons"
                                size={"large"}
                                disabled={isCallNotIdle || !hasPhone}
                                primary
                            />
                        </ButtonLink>
                    ) : (
                        <Popup
                            className="AddForm"
                            trigger={
                                <ButtonLink>
                                    <Icon
                                        className="fa-solid fa-phone OfficeView__icons"
                                        size={"large"}
                                        primary
                                        disabled={isCallNotIdle || !hasPhone}
                                    />
                                </ButtonLink>
                            }
                            content={() =>
                                logActivityForm(
                                    {
                                        id: person?.contact_id || person?.account_id,
                                    },
                                    false,
                                    person?.contact_id ? true : false,
                                    () => setIsCallActivityFormOpen(false),
                                    true
                                )
                            }
                            closeOnDocumentClick={false}
                            open={isCallActivityFormOpen}
                            onClose={() => setIsCallActivityFormOpen(false)}
                            onOpen={() => setIsCallActivityFormOpen(true)}
                            on="click"
                            position="bottom left"
                            pinned
                            basic
                            wide
                        />
                    )}
                    
                    {!isInternalCall && hasIntegration ? (
                        <ButtonLink onClick={onEmailClick} disabled={isEmailDisabled}>
                            <Icon className='fa-solid fa-envelope OfficeView__icons' size={'large'} />
                        </ButtonLink>
                    ) : (
                        <Popup
                            className="AddForm"
                            trigger={
                                <ButtonLink onClick={() => setIsLogActivityModalOpen(true)} disabled={isEmailDisabled}>
                                    <Icon className='fa-solid fa-envelope OfficeView__icons' size={'large'} />
                                </ButtonLink>
                            }
                            content={() =>
                                logActivityForm(
                                    {
                                        id: person.contact_id,
                                        email: {
                                            email: person?.contact_id ? person.contact_details.email : person?.account_details?.email,
                                            main: true,
                                        },
                                    },
                                    true,
                                    false,
                                    () => setIsLogActivityModalOpen(false)
                                )
                            }
                            open={isLogActivityModalOpen}
                            on="click"
                            position="bottom left"
                            offset={[25, -125]}
                            pinned
                            basic
                            wide
                        />
                    )}
                    {!isInternalCall && (person?.contact_id && !isUnknown) &&(
                        <>
                            <ButtonLink onClick={onTaskClick}>
                                <Icon className='fa-solid fa-clipboard-check OfficeView__icons' size={'large'} />
                            </ButtonLink>
                            <ButtonLink onClick={onEventClick}>
                                <Icon className='fa-solid fa-calendar-day OfficeView__icons' size={'large'} />
                            </ButtonLink>
                            <ButtonLink onClick={onHotlistClick}>
                                <Icon className={`${isInHotlist ? "fa-solid fa-fire-flame-curved OfficeView__icons--red" : "fa-solid fa-fire-flame OfficeView__icons"}`} size={'large'} />
                            </ButtonLink>
                        </>
                    )}
                </div>
            }
            {(isUnknown && !isInternalCall) && (
                <RestrictedView type="link" userPermission={USER_PERMISSIONS.CONTACTS_ADD} productPermission={PRODUCT_PERMISSIONS.RECORDS}>
                    <ButtonLink style={{ marginTop: 20 }} onClick={onNewContactClick}>
                            <Icon className='fa-solid fa-plus OfficeView__unknownAddIcon' circular />
                        <strong style={{ marginLeft: 5 }}>New/Existing Contact</strong>
                    </ButtonLink>
                </RestrictedView>

            )}
        </div>
    )
}

export default OfficeViewMiddleHeader;
